<template>
    <div>
        <v-card
            v-if="!success"
            width="400"
            class="mx-auto mt-5 pt-5 px-8"
        >
            <div class="d-flex justify-center">
                <img src="../assets/reset-password.png">
            </div>
            <v-card-title class="d-flex justify-center card-title">
                Reset Your Password
            </v-card-title>
            <v-card-text>
                <v-card
                    class="mx-auto"
                >
                    <v-card-text class="rule-list">
                        <div class="instructions">
                            Password must have the following:
                        </div>
                        <ul>
                            <li
                                v-for="rule of rules"
                                :key="rule.description"
                                class="rule-description"
                                :class="[ !password ? '' : rule.test(password) ? 'rule-passed' : 'rule-failed' ]"
                            >
                                {{ rule.description }}
                            </li>
                        </ul>
                    </v-card-text>
                </v-card>
                <v-form class="mt-8">
                    <v-alert
                        v-model="missingPassword"
                        dense
                        dismissible
                        text
                        type="error"
                        icon="info_outline"
                        class="errorAlert"
                        close-icon="close"
                    >
                        Enter new password to reset
                    </v-alert>
                    <v-alert
                        v-model="tokenValidationFailure"
                        dense
                        dismissible
                        text
                        type="error"
                        icon="info_outline"
                        class="errorAlert"
                        close-icon="close"
                    >
                        This reset password link is invalid, expired, or has already been used.
                    </v-alert>
                    <v-alert
                        v-model="resetPasswordFailure"
                        dense
                        dismissible
                        text
                        type="error"
                        icon="info_outline"
                        class="errorAlert"
                        close-icon="close"
                    >
                        The reset password attempt failed. Contact your administrator.
                    </v-alert>
                    <v-text-field
                        v-model="password"
                        outlined
                        :type="showPassword ? 'text' : 'password' "
                        label="New Password"
                        prepend-inner-icon="lock_outline"
                        :error="missingPassword"
                        @keyup="passwordInputKeyUp"
                    >
                        <template v-slot:append>
                            <v-icon
                                class="material-icons-outlined"
                                @click="showPassword = !showPassword"
                            >
                                {{ getPasswordVisibilityIcon }}
                            </v-icon>
                        </template>
                    </v-text-field>
                    <v-text-field
                        v-model="passwordConfirm"
                        outlined
                        :type="showPasswordConfirm ? 'text' : 'password' "
                        label="Confirm Password"
                        prepend-inner-icon="lock_outline"
                        :error-messages="password !== passwordConfirm ? 'Password and confirm password must be match.' : ''"
                    >
                        <template v-slot:append>
                            <v-icon
                                class="material-icons-outlined"
                                @click="showPasswordConfirm = !showPasswordConfirm"
                            >
                                {{ getPasswordConfirmVisibilityIcon }}
                            </v-icon>
                        </template>
                    </v-text-field>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-row class="px-5">
                    <v-btn
                        height="48px"
                        color="primary"
                        block
                        :disabled="password !== passwordConfirm"
                        @click="changePassword"
                    >
                        Reset Password
                    </v-btn>
                </v-row>
            </v-card-actions>
            <div class="d-flex justify-center mt-5 pb-5 logo">
                <img src="../assets/logo.png">
            </div>
        </v-card>
        <v-card
            v-else
            width="400"
            class="mx-auto mt-5 pt-12 px-8"
        >
            <div class="d-flex justify-center">
                <img src="../assets/pw-changed.png">
            </div>
            <v-card-title class="d-flex justify-center card-title">
                Password Changed!
            </v-card-title>
            <v-card-subtitle class="d-flex justify-center successText">
                You can use your new password to Sign In to your account.
            </v-card-subtitle>
            <v-card-actions>
                <router-link
                    to="/"
                    class="d-flex signInButton mt-10"
                >
                    <v-btn
                        color="primary"
                        width="100%"
                    >
                        Back to Sign In
                    </v-btn>
                </router-link>
            </v-card-actions>
            <div class="d-flex justify-center mt-5 pb-5 logo">
                <img src="../assets/logo.png">
            </div>
        </v-card>
    </div>
</template>

<script>
import { getStrengthRules } from '@/utils/password';
import webtmsBackend from '@/services/webtmsBackend';

export default {
    data: () => ({
        rules: [],
        showPassword: false,
        showPasswordConfirm: false,
        password: '',
        passwordConfirm: '',
        missingPassword: false,
        success: false,
        tokenValidationFailure: false,
        resetPasswordFailure: false,
    }),
    computed: {
        getPasswordVisibilityIcon(){
            return this.showPassword ? 'visibility': 'visibility_off';
        },
        getPasswordConfirmVisibilityIcon(){
            return this.showPasswordConfirm ? 'visibility': 'visibility_off';
        }
    },
    mounted: async function() {
        const token = this.$route.query.v;

        try {
            const { passwordStrength } = await webtmsBackend.validateResetPasswordToken(token);

            this.rules = await getStrengthRules(passwordStrength.toUpperCase());
        } catch (error) {
            console.error(error);
            this.tokenValidationFailure = true;
        }
    },
    methods: {
        changePassword: async function () {
            if (!this.password) {
                this.missingPassword = true;
            } else {
                try {
                    const token = this.$route.query.v;

                    // call the password change service with token and new password
                    const { status } = await webtmsBackend.resetPasswordWithToken(token, this.password);

                    if (status === 'OK') {
                        this.success = true;
                    } else {
                        this.resetPasswordFailure = true;
                    }
                } catch (error) {
                    console.error(error);
                    this.resetPasswordFailure = true;
                }
            }
        },
        passwordInputKeyUp: function () {
            if (this.password) {
                this.missingPassword = false;
            }
        }
    }
};
</script>

<style scoped>
.card-title {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0.0025em;
    color: var(--ll-text-primary);
}
.rule-list {
    background: var(--ll-surface-subtle);
    border-radius: 4px;
    color: var(--ll-text-primary);
    font-style: normal;
}
.instructions {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.0025em;
}
.rule-description {
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
    letter-spacing: 0.004em;
}
.rule-passed {
    color: #2FA84F;
}
.rule-failed {
    color: #CB3D31;
}
.errorAlert {
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0.004em;
    color: #CB3D31;
}
.logo img {
    width: 107px;
}
.successText {
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;

    display: flex;
    align-items: center;
    text-align: center;
    letter-spacing: 0.0025em;

    color: #5F6470;
}
.signInButton {
    width: 100%;
}
</style>
